import { useEffect, useRef, useState } from 'react';

import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';

import useContentAccessLevel from 'src/hooks/useContentAccessLevel';

import { ComponentProps, SitecoreContextValue } from 'lib/ComponentProps';

import { parseAccessLevelRouteFields } from 'src/mappers/fields/parseAccessLevelContent';

import { ResourceHeadings } from 'src/types/ResourceListItem';

import { toKebabCase } from 'src/utils/helpers';

import Accordion from 'src/components/basicContent/_Accordion/Accordion';
import SlideOut from 'src/components/basicContent/_SlideOut/_SlideOut';

import ChevronDownIcon from 'public/icons/ChevronDown.svg';

import styles from './ResourceNavigation.module.css';

type ResourceNavigationProps = ComponentProps & {
	fields: {
		ResourceHeadings: ResourceHeadings[];
		ResourceTitle: string;
	};
	showNavigationContainer: boolean;
	setShowNavigationContainer: (arg0: boolean) => void;
};

export default function ResourceNavigation(props: ResourceNavigationProps): JSX.Element {
	const { sitecoreContext } = useSitecoreContext<SitecoreContextValue>();
	const access = parseAccessLevelRouteFields(sitecoreContext.route.fields);
	const { isAllowedAccessToContent } = useContentAccessLevel(access);

	const [activeSection, setActiveSection] = useState<string>('');
	const lastActiveRef = useRef<string>('');

	useEffect(() => {
		if (typeof document === 'undefined') {
			return;
		}

		const currentPage = props.fields?.ResourceHeadings.find((ResourceHeading) => ResourceHeading.isCurrentPage);

		// Collect your section headers (the H3s you showed)
		const sections = document.querySelectorAll('[data-section-header]');

		// Set an initial active id (first section or fallback to page slug)
		const initialId = sections[0]?.id ?? toKebabCase(currentPage?.text || '');
		lastActiveRef.current = initialId;
		setActiveSection(initialId);

		let newActiveSection = false;
		function scrollHandler() {
			if (newActiveSection) {
				return;
			}

			newActiveSection = true;

			// Start assuming the top-most section is active, then walk down
			let currentId = sections[0]?.id ?? lastActiveRef.current;

			for (let i = 0; i < sections.length; i++) {
				const element = sections[i];
				const top = element.getBoundingClientRect().top;

				// If this heading has crossed above the activation line, it's the new active
				if (top - 120 <= 0) {
					currentId = element.id;
				} else {
					// As soon as we find one below the line, stop. The last above remains active.
					break;
				}
			}

			if (currentId && currentId !== lastActiveRef.current) {
				lastActiveRef.current = currentId;
				setActiveSection(currentId);
			}

			newActiveSection = false;
		}

		// Run once so the correct section is set after initial render/layout
		scrollHandler();

		window.addEventListener('scroll', scrollHandler, { passive: true });
		return () => window.removeEventListener('scroll', scrollHandler);
	}, [props.fields?.ResourceHeadings]);

	return (
		<SlideOut
			fromLeft={true}
			slideOutState={props.showNavigationContainer}
			setSlideOutState={props.setShowNavigationContainer}
			destroy={true}
		>
			{props.fields.ResourceHeadings?.map((resourceHeading, index) => {
				return (
					<section key={`${resourceHeading.text}-${index}`}>
						<Accordion
							headerClass={styles.resourceNavigationAccordionHeader}
							bodyClass={styles.resourceNavigationAccordionBody}
							sectionClass={styles.resourceNavigationAccordion}
							setOpen={resourceHeading.headings
								.map((heading) => toKebabCase(heading.navigationLinkText || heading.text))
								.includes(activeSection)}
						>
							<span className={styles.resourceNavigationAccordionHeaderContent}>
								<span>
									<ChevronDownIcon className={styles.resourceNavigationAccordionIcon} />
								</span>
								<span className={styles.resourceNavigationAccordionHeaderContentText}>{resourceHeading.pageTitle}</span>
							</span>
							<div className={styles.resourceNavigationAccordionBodyContent}>
								{resourceHeading.headings?.map((heading, index) => {
									return (
										<a
											key={`${heading.url}-${index}`}
											href={heading.url}
											target="_self"
											aria-label={heading.navigationLinkText?.trim() || heading.text?.trim() || `Navigate to ${heading.url}`}
											className={`${styles.resourceNavigationAccordionBodyContentLinks} 
															${activeSection === toKebabCase(heading.text) ? styles.activeStyle : ''} 
															${!isAllowedAccessToContent && 'disabled'}`}
										>
											{heading.navigationLinkText ? heading.navigationLinkText : heading.text}
										</a>
									);
								})}
							</div>
						</Accordion>
					</section>
				);
			})}
		</SlideOut>
	);
}
